/**
 * Canonical `product_updated` editable field list (BUG22).
 *
 * One source of truth for the editable HouseholdProduct attribute set carried
 * by a `product_updated` sync event. THREE surfaces must cover exactly these
 * fields or a cross-device edit silently drops on the live + replay paths:
 *
 *   1. The server REST emit — `householdProductCatalogService.updateHouseholdProduct`
 *      builds the `product_updated` `newValue` payload from this set.
 *   2. The client-engine projection — `engine.ts::applyHouseholdProductEventInner`
 *      (`product_updated` case) imports this list and `copyIfPresent`s it onto
 *      the local row. This is the reference projector.
 *   3. The server projection — `syncService.ts::applyHouseholdProductEvent`
 *      (`product_updated` case) re-derives the same set on a device→server replay.
 *
 * Wire keys are snake_case (the `/api/v2/sync` `newValue` convention — every
 * emit and every consumer agrees on snake_case; see the engine test's
 * `{ display_name: ... }` payloads and the REST emit in
 * `householdProductCatalogService.ts`). `null` clears, an absent key leaves —
 * the engine projector keys off `key in source`, the emit + server projection
 * off `!== undefined`.
 *
 * The backend cannot import this package at build time (no `@shopista/shared`
 * dependency on the backend by design — see `backend/src/household/syncEventBus.ts`),
 * so the backend keeps a local copy of this list and a parity test
 * (`backend/tests/product-updated-field-parity.test.ts`) pins the backend copy,
 * the emit-payload keys, and the server-projection coverage against THIS
 * canonical const — so the three sides cannot drift again.
 *
 * Each entry is a key of `HouseholdProduct` so the engine's typed
 * `copyIfPresent<HouseholdProduct>` keeps its `keyof HouseholdProduct` typing.
 */

import type { HouseholdProduct } from '../types';

export const PRODUCT_UPDATED_FIELDS = [
  'display_name',
  'category_id',
  'notes',
  'tags',
  'image_url',
  'default_quantity',
  'cadence_days',
  'allow_substitutions',
  'substitution_notes',
  'always_keep_stocked',
  'expected_price_value',
  'expected_price_currency',
  'expected_price_notes',
  // T36.3 / ADR-015 §6 — resolved measurement snapshot. Server-resolved (not a
  // body-driven edit), but it rides the SAME `product_updated` event so a link /
  // picker change projects live onto the cached row; the engine keys off
  // `key in newValue`, so a server-built payload carrying these projects fine.
  'measurement_pack_count',
  'measurement_value',
  'measurement_unit',
  'measurement_dimension',
  'measurement_forms',
  'measurement_provenance',
] as const satisfies readonly (keyof HouseholdProduct & string)[];

export type ProductUpdatedField = (typeof PRODUCT_UPDATED_FIELDS)[number];
